import React from 'react';
import Icon from '../../../components/AppIcon';

const OrderStatsPanel = ({ orders }) => {
  const calculateStats = () => {
    const totalSpent = orders?.reduce((sum, order) => sum + order?.totalAmount, 0);
    const completedOrders = orders?.filter(order => order?.status === 'completed')?.length;
    const pendingOrders = orders?.filter(order => order?.status === 'pending' || order?.status === 'in-progress')?.length;
    
    // Calculate favorite service category
    const categoryCount = {};
    orders?.forEach(order => {
      order?.items?.forEach(item => {
        categoryCount[item.category] = (categoryCount?.[item?.category] || 0) + 1;
      });
    });
    
    const favoriteCategory = Object.keys(categoryCount)?.reduce((a, b) => 
      categoryCount?.[a] > categoryCount?.[b] ? a : b, 'N/A'
    );

    // Calculate average order value
    const avgOrderValue = orders?.length > 0 ? totalSpent / orders?.length : 0;

    // Calculate loyalty points (mock calculation)
    const loyaltyPoints = Math.floor(totalSpent * 0.01);

    return {
      totalSpent,
      completedOrders,
      pendingOrders,
      favoriteCategory,
      avgOrderValue,
      loyaltyPoints,
      totalOrders: orders?.length
    };
  };

  const stats = calculateStats();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const statCards = [
    {
      title: 'Total Spent',
      value: formatCurrency(stats?.totalSpent),
      icon: 'DollarSign',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      title: 'Total Orders',
      value: stats?.totalOrders?.toString(),
      icon: 'ShoppingBag',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      title: 'Completed',
      value: stats?.completedOrders?.toString(),
      icon: 'CheckCircle',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      title: 'Pending',
      value: stats?.pendingOrders?.toString(),
      icon: 'Clock',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    },
    {
      title: 'Average Order',
      value: formatCurrency(stats?.avgOrderValue),
      icon: 'TrendingUp',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      title: 'Loyalty Points',
      value: stats?.loyaltyPoints?.toLocaleString(),
      icon: 'Star',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="BarChart3" size={20} className="text-muted-foreground" />
        <h3 className="text-lg font-semibold text-foreground">Order Statistics</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {statCards?.map((stat, index) => (
          <div key={index} className="flex items-center space-x-3 p-4 bg-muted/30 rounded-lg">
            <div className={`p-2 rounded-lg ${stat?.bgColor}`}>
              <Icon name={stat?.icon} size={20} className={stat?.color} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{stat?.title}</p>
              <p className="text-lg font-semibold text-foreground">{stat?.value}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Favorite Category */}
      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Heart" size={16} className="text-error" />
            <span className="text-sm text-muted-foreground">Favorite Service Category:</span>
          </div>
          <span className="text-sm font-medium text-foreground capitalize">
            {stats?.favoriteCategory}
          </span>
        </div>
      </div>
      {/* Loyalty Program Info */}
      <div className="mt-4 p-4 bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg border border-primary/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Crown" size={16} className="text-warning" />
            <span className="text-sm font-medium text-foreground">TechTribes Loyalty Program</span>
          </div>
          <span className="text-sm text-primary font-medium">
            {stats?.loyaltyPoints} points available
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Earn 1 point for every UGX 100 spent. Redeem points for discounts on future orders.
        </p>
      </div>
    </div>
  );
};

export default OrderStatsPanel;